/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:06]
Permissions to update outside the main branch: [false]
*/

import {
    Filter,
    Input,
    parseToInt,
    Pending,
    toDisplayRoleName,
    Tree,
    TreeNodeType,
    useErrorState,
} from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getRolesByParentId, getRolesList } from 'axios/roleApi';
import useApi from 'hooks/useApi';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { Role, RoleFilter as RoleFilterType, RoleTableItem } from 'models/api/roles';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Card, CardHeader, Collapse, FormGroup } from 'reactstrap';
import { roleTableItemToRole } from 'utils/formatters';
import { useUserWorkplaceState } from '../Users';
import UserRoleFilter from './UserRoleFilter';

type RolesTabProps = {
    roles: Role[];
    setRoles: Dispatch<SetStateAction<Role[]>>;
    disabled: boolean;
};

const emptyFilter: Filter<RoleFilterType> = {
    id: null,
    name: '',
    displayName: '',
    dsc: '',
    isActive: null,
    typeId: null,
    level: 0,
};

const RolesTab = ({ roles, setRoles, disabled }: RolesTabProps) => {
    const api = useApi();
    const { t, i18n } = useTranslation();
    const { handleRequestError } = useErrorState();
    const { settings } = useGeneralSettingsContext();
    const { currentItem } = useUserWorkplaceState();

    const [isOpenFilter, setIsOpenFilter] = useState(false);
    const toggleFilter = () => setIsOpenFilter(!isOpenFilter);

    const [filter, setFilter] = useState(emptyFilter);

    const [allRoles, setAllRoles] = useState<RoleTableItem[]>([]);

    const { isFetching: loadingAllRoles } = useQuery(
        ['allRoles', filter],
        () => getRolesList(api, filter),
        {
            onSuccess: (data) => {
                setAllRoles(data.data);
            },
            onError: (err: AxiosError<AxiosErrorResponse>) => {
                handleRequestError(err);
            },
        }
    );

    const maxUserRoles = useMemo(
        () =>
            parseToInt(
                settings
                    .find((x) => x.code === 'MaxUserRoles')
                    ?.translations.find((x) => x.cultureCode === i18n.language)?.value,
                0
            ),
        [settings]
    );

    const roleDisabled = (checked: boolean): boolean =>
        !!(!checked && roles && roles.length >= maxUserRoles);

    const changeRole = (role: RoleTableItem) => {
        const r = roles.find((x) => x.id === role.id);

        setRoles((prev) => {
            return r ? prev.filter((x) => x !== r) : [...prev, roleTableItemToRole(role)];
        });
    };

    const roleToNode = (role: RoleTableItem): ReactNode => {
        const title = toDisplayRoleName(role.name, t);
        return !role.isActive ? (
            <i className="text-secondary">
                {title} ({t('BackOfficeInactive')})
            </i>
        ) : (
            <span>{title}</span>
        );
    };

    const getChildrenFunc = (role: RoleTableItem) => () =>
        getRolesByParentId(api, role.id?.toString() || '').then((roles) =>
            roles.data.map<TreeNodeType>((r: RoleTableItem) => ({
                id: r.id,
                inner: roleToNode(r),
                hasChildren: r.hasChildren,
                getChildren: getChildrenFunc(r),
            }))
        );

    useEffect(() => {
        setRoles(currentItem != null ? currentItem.roles || [] : []);
    }, [currentItem]);

    return (
        <>
            {roles && roles.length >= maxUserRoles && (
                <div className="user-modal__roles-count-error">
                    {t('BackOfficeUserRolesLimitError')}
                </div>
            )}
            <Card className="mb-3">
                <CardHeader className="cursor--pointer" onClick={toggleFilter}>
                    {t('BackOfficeRolesSearch')}
                </CardHeader>

                <Collapse isOpen={isOpenFilter}>
                    <UserRoleFilter setFilter={setFilter} defautValues={emptyFilter} />
                </Collapse>
            </Card>
            {loadingAllRoles ? (
                <Pending />
            ) : (
                allRoles.map((role) => {
                    const checked = !!roles.find((r) => r.id === role.id);
                    return (
                        <FormGroup check key={role.id}>
                            <Input
                                id={`checkbox-${role.id}`}
                                type="checkbox"
                                className="d-inline role-checkbox"
                                inputClassName="cursor--pointer"
                                checked={checked}
                                onChange={() => changeRole(role)}
                                disabled={disabled || roleDisabled(checked)}
                                label={
                                    <Tree
                                        id={role.id?.toString() || ''}
                                        inner={roleToNode(role)}
                                        hasChildren={role.hasChildren}
                                        getChildren={getChildrenFunc(role)}
                                    />
                                }
                            />
                        </FormGroup>
                    );
                })
            )}
        </>
    );
};

export default RolesTab;
